import { useTranslation } from 'react-i18next'

import {
  PaymentChargeType,
  TransactionStatus,
} from '#/shared/api/generated/billing/models'
import type {
  FileResponse,
  PaymentDetail,
  PaymentTransactionResponse,
} from '#/shared/api/generated/billing/models'
import { useGetPaymentApiV1PaymentsPaymentIdGet } from '#/shared/api/generated/billing/theCargoBilling'
import {
  formatBytes,
  formatLongDate,
  formatRelativeTime,
  formatTime,
  formatUsd,
} from '#/shared/lib/format'

const FALLBACK_DASH = '—'
const DETAIL_PREFIX = 'orderDetails.comment.payment.detail'

type Translate = ReturnType<typeof useTranslation>['t']

export interface PaymentProofFile {
  id: string
  name: string
  meta: string
  ext: string
  url: string
  isImage: boolean
}

export interface PaymentDetailRowView {
  label: string
  value: string
  strong: boolean
}

export interface PaymentTransactionView {
  id: string
  title: string
  canSendReceipt: boolean
  rows: ReadonlyArray<PaymentDetailRowView>
  files: ReadonlyArray<PaymentProofFile>
}

export interface PaymentDetailView {
  amount: string
  description: string
  meta: string
  receiptEmail: string | undefined
  transactions: ReadonlyArray<PaymentTransactionView>
}

export interface UsePaymentDetailResult {
  view: PaymentDetailView | undefined
  isLoading: boolean
  isError: boolean
}

const TX_STATUS_KEY: Record<TransactionStatus, string> = {
  [TransactionStatus.succeeded]: 'succeeded',
  [TransactionStatus.pending]: 'pending',
  [TransactionStatus.failed]: 'failed',
}

function parseAmount(raw: number | string | null | undefined): number {
  const value = Number(raw)
  return Number.isFinite(value) ? value : 0
}

function displayAmount(detail: PaymentDetail): string {
  const charged = parseAmount(detail.amount_charged)
  const total = charged > 0 ? charged : parseAmount(detail.total_amount)
  return formatUsd(total, { showCents: true })
}

function heroAmount(detail: PaymentDetail, t: Translate): string {
  const charged = parseAmount(detail.amount_charged)
  const total = parseAmount(detail.total_amount)
  const collected = formatUsd(charged > 0 ? charged : total, {
    showCents: true,
  })
  if (charged > 0 && charged < total) {
    return t(`${DETAIL_PREFIX}.amountOf`, {
      amount: collected,
      total: formatUsd(total, { showCents: true }),
    })
  }
  return collected
}

function methodLabelFor(
  methodType: string | null | undefined,
  cardBrand: string | null | undefined,
  cardLast4: string | null | undefined,
  t: Translate,
): string {
  if (!methodType) return FALLBACK_DASH
  const label = t(`${DETAIL_PREFIX}.method.${methodType}`, {
    defaultValue: methodType,
  })
  if (methodType === PaymentChargeType.card && cardLast4) {
    return `${cardBrand ?? label} ···· ${cardLast4}`
  }
  return label
}

function receivedAt(iso: string, t: Translate): string {
  return t(`${DETAIL_PREFIX}.receivedAt`, {
    date: formatLongDate(iso),
    time: formatTime(iso),
  })
}

function buildMeta(detail: PaymentDetail, count: number, t: Translate): string {
  if (count > 1) return t(`${DETAIL_PREFIX}.splitCount`, { count })
  const method = methodLabelFor(
    detail.method_type,
    detail.card_brand,
    detail.card_last4,
    t,
  )
  const iso = detail.paid_at ?? detail.paid_date ?? detail.created_at
  return t(`${DETAIL_PREFIX}.heroMeta`, {
    method,
    date: formatLongDate(iso),
    time: formatTime(iso),
  })
}

function fileExt(file: FileResponse): string {
  const fromName = file.filename.split('.').pop()
  if (fromName && fromName !== file.filename) return fromName.toUpperCase()
  const sub = (file.content_type ?? '').split('/').pop()
  return (sub ?? 'file').toUpperCase()
}

function toFileView(file: FileResponse, t: Translate): PaymentProofFile {
  return {
    id: file.id,
    name: file.filename,
    ext: fileExt(file),
    url: file.url,
    isImage: (file.content_type ?? '').startsWith('image/'),
    meta: t(`${DETAIL_PREFIX}.proofMeta`, {
      size: formatBytes(file.size_bytes),
      when: formatRelativeTime(file.created_at),
    }),
  }
}

function buildRows(
  status: string,
  method: string,
  amount: string,
  iso: string,
  reference: string | undefined,
  recordedBy: string | undefined,
  t: Translate,
): PaymentDetailRowView[] {
  const rows: PaymentDetailRowView[] = [
    { label: t(`${DETAIL_PREFIX}.rows.status`), value: status, strong: true },
  ]
  if (method !== FALLBACK_DASH) {
    rows.push({
      label: t(`${DETAIL_PREFIX}.rows.method`),
      value: method,
      strong: true,
    })
  }
  rows.push(
    { label: t(`${DETAIL_PREFIX}.rows.amount`), value: amount, strong: true },
    {
      label: t(`${DETAIL_PREFIX}.rows.received`),
      value: receivedAt(iso, t),
      strong: false,
    },
  )
  if (reference) {
    rows.push({
      label: t(`${DETAIL_PREFIX}.rows.reference`),
      value: reference,
      strong: false,
    })
  }
  if (recordedBy) {
    rows.push({
      label: t(`${DETAIL_PREFIX}.rows.recordedBy`),
      value: recordedBy,
      strong: false,
    })
  }
  return rows
}

function transactionTitle(
  tx: PaymentTransactionResponse,
  count: number,
  t: Translate,
): string {
  if (count <= 1) return t(`${DETAIL_PREFIX}.paymentDetails`)
  const ref = (tx.transaction_id ?? '').trim()
  return ref ? t(`${DETAIL_PREFIX}.paymentRef`, { id: ref }) : tx.id
}

function toTransactionView(
  tx: PaymentTransactionResponse,
  count: number,
  t: Translate,
): PaymentTransactionView {
  return {
    id: tx.id,
    title: transactionTitle(tx, count, t),
    canSendReceipt: tx.status === TransactionStatus.succeeded,
    rows: buildRows(
      t(`${DETAIL_PREFIX}.txStatus.${TX_STATUS_KEY[tx.status]}`),
      methodLabelFor(tx.method_type, tx.card_brand, tx.card_last4, t),
      formatUsd(parseAmount(tx.amount), { showCents: true }),
      tx.paid_at ?? tx.created_at,
      tx.reference ?? undefined,
      tx.settled_by ?? undefined,
      t,
    ),
    files: (tx.files ?? []).map((file) => toFileView(file, t)),
  }
}

function fallbackTransaction(
  detail: PaymentDetail,
  t: Translate,
): PaymentTransactionView {
  return {
    id: detail.id,
    title: t(`${DETAIL_PREFIX}.paymentDetails`),
    canSendReceipt: true,
    rows: buildRows(
      t(`${DETAIL_PREFIX}.txStatus.succeeded`),
      methodLabelFor(
        detail.method_type,
        detail.card_brand,
        detail.card_last4,
        t,
      ),
      displayAmount(detail),
      detail.paid_at ?? detail.paid_date ?? detail.created_at,
      detail.reference ?? detail.reference_id ?? undefined,
      detail.settled_by ?? undefined,
      t,
    ),
    files: [],
  }
}

function toView(detail: PaymentDetail, t: Translate): PaymentDetailView {
  const isRefund = detail.status === 'refunded'
  const txns = detail.transactions ?? []
  return {
    amount: heroAmount(detail, t),
    description: t(
      `${DETAIL_PREFIX}.headline.${isRefund ? 'refunded' : 'received'}`,
    ),
    meta: buildMeta(detail, txns.length, t),
    receiptEmail: detail.receipt_email ?? undefined,
    transactions:
      txns.length > 0
        ? txns.map((tx) => toTransactionView(tx, txns.length, t))
        : [fallbackTransaction(detail, t)],
  }
}

export function usePaymentDetail(
  paymentId: string,
  enabled: boolean,
): UsePaymentDetailResult {
  const { t } = useTranslation()
  const query = useGetPaymentApiV1PaymentsPaymentIdGet(paymentId, {
    query: { enabled: enabled && Boolean(paymentId) },
  })

  const detail = query.data?.data
  return {
    view: detail ? toView(detail, t) : undefined,
    isLoading: query.isLoading && enabled,
    isError: query.isError,
  }
}
